import {
	CostAllocation,
	CostAllocationService,
	CostGroupCategory,
	CustomCostAllocationService,
	OrdinaryCostAllocationService,
	ShareCostAllocationService,
	TenantSelectionService,
	TenantShareService,
} from '@eadsysteme/billingclient-v1';
import { AsyncCache } from '../util/AsyncCache';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { SessionService } from './session.service';
import { CostAllocationDetail } from './data/CostAllocationDetail';
import { CostAllocationDataType, getAllocationTypeLabelKey, isOrdinaryAllocation, isShareAllocation } from '../util/models/costAllocation/cost-allocation-type';

/**
 * Zentrale Fassade für Umlagen (CostAllocation):
 * - readAll liefert nur Basis-Felder (name, costGroupCategory, dtype, exportId, tenantSelectionExportId);
 *   Subtyp-Felder (Ordinary.type, Share.tenantShareExportId) werden hier per typspezifischem
 *   GET-by-ID nachgeladen und gecacht.
 * - Alle Consumer (Übersicht, Cost-Dialoge) laden über loadAllocationDetails dasselbe Anzeige-Modell.
 */
@Injectable({ providedIn: 'root' })
export class CostAllocationHelper {
	private readonly costAllocationService = inject(CostAllocationService);
	private readonly shareCostAllocationService = inject(ShareCostAllocationService);
	private readonly ordinaryCostAllocationService = inject(OrdinaryCostAllocationService);
	private readonly customCostAllocationService = inject(CustomCostAllocationService);
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly sessionService = inject(SessionService);
	private readonly transloco = inject(TranslocoService);

	private readonly selectionCache = new AsyncCache<string>();
	private readonly shareCache = new AsyncCache<string>();
	private readonly allocationCache = new AsyncCache<CostAllocation>();

	/**
	 * Lädt alle Umlagen einer Periode als angereicherte Details:
	 * readAll → optionaler Kategorie-Filter → Subtyp-Detail-Fetch → Namen (Selection immer, Share nur bei Share-Umlagen).
	 */
	public async loadAllocationDetails(billingExportId: string, billingPeriodExportId: string, category?: CostGroupCategory): Promise<CostAllocationDetail[]> {
		const all = await firstValueFrom(
			this.costAllocationService.readAllCostAllocation(this.sessionService.effectiveArea, billingExportId, billingPeriodExportId)
		);
		const filtered = category == null ? (all ?? []) : (all ?? []).filter((a) => a.costGroupCategory === category);
		return Promise.all(filtered.map((base) => this.toDetail(base, billingExportId)));
	}

	/**
	 * Lädt das vollständige Subtyp-DTO einer Umlage anhand ihres dtype (gecacht per exportId).
	 * Notwendig, weil readAll nur die Basis-Felder liefert.
	 */
	public async loadFullAllocation(base: CostAllocation, billingExportId: string): Promise<CostAllocation> {
		if (!base?.exportId) {
			return base;
		}
		try {
			return await this.allocationCache.get(base.exportId, () => this.readByType(base, billingExportId));
		} catch {
			return base;
		}
	}

	/** Cache-Invalidierung nach Update/Delete einer Umlage. */
	public invalidateAllocation(exportId: string | undefined): void {
		if (exportId) {
			this.allocationCache.delete(exportId);
		}
	}

	/** Cache-Invalidierung nach Umbenennung eines Nutzeranteils (z. B. im Parameter-Dialog). */
	public invalidateTenantShareName(exportId: string | undefined): void {
		if (exportId) {
			this.shareCache.delete(exportId);
		}
	}

	/** Cache-Invalidierung nach Umbenennung einer Nutzerauswahl (z. B. im Parameter-Dialog). */
	public invalidateTenantSelectionName(exportId: string | undefined): void {
		if (exportId) {
			this.selectionCache.delete(exportId);
		}
	}

	/**
	 * Zentrale Anzeigenamen-Logik: name falls gesetzt, sonst bei Ordinary der übersetzte
	 * Verteilungstyp, sonst bei Share der Name des Nutzeranteils.
	 */
	public getAllocationDisplayName(allocation: CostAllocationDetail | CostAllocation | null | undefined): string {
		if (!allocation) {
			return '';
		}
		if (allocation.name) {
			return allocation.name;
		}
		// ordinaryType/shareName existieren nur am angereicherten Detail; ein reines
		// CostAllocation lässt sie hier undefined (→ '') statt sie per Cast vorzutäuschen.
		const detail: Partial<CostAllocationDetail> = allocation;
		const ordinaryType = isOrdinaryAllocation(allocation) ? allocation.type : detail.ordinaryType;
		if (ordinaryType != null) {
			return this.transloco.translate('billing.ordinaryAllocationType.' + ordinaryType);
		}
		return detail.shareName ?? '';
	}

	private async loadName<T extends { name?: string }>(
		id: string | null,
		cache: AsyncCache<string>,
		serviceFn: (id: string) => Observable<T>
	): Promise<string | null> {
		if (!id) return null;

		try {
			return await cache.get(id, () =>
				serviceFn(id).pipe(
					map((result) => result?.name ?? ''),
					catchError(() => of(''))
				)
			);
		} catch {
			return '';
		}
	}

	private readByType(base: CostAllocation, billingExportId: string): Observable<CostAllocation> {
		const area = this.sessionService.effectiveArea;
		switch (base.dtype) {
			case CostAllocationDataType.SHARE:
				return this.shareCostAllocationService.readShareCostAllocation(area, billingExportId, base.exportId);
			case CostAllocationDataType.ORDINARY:
				return this.ordinaryCostAllocationService.readOrdinaryCostAllocation(area, billingExportId, base.exportId);
			case CostAllocationDataType.CUSTOM:
				return this.customCostAllocationService.readCustomCostAllocation(area, billingExportId, base.exportId);
			default:
				return of(base);
		}
	}

	private async toDetail(base: CostAllocation, billingExportId: string): Promise<CostAllocationDetail> {
		const full = await this.loadFullAllocation(base, billingExportId);
		const tenantShareExportId = isShareAllocation(full) ? full.tenantShareExportId : undefined;
		const ordinaryType = isOrdinaryAllocation(full) ? full.type : undefined;

		const [selectionName, shareName] = await Promise.all([
			this.loadName(full.tenantSelectionExportId ?? null, this.selectionCache, (id) =>
				this.tenantSelectionService.readTenantSelection(this.sessionService.effectiveArea, billingExportId, id)
			),
			tenantShareExportId == null
				? Promise.resolve<string | null>(null)
				: this.loadName(tenantShareExportId, this.shareCache, (id) =>
						this.tenantShareService.readTenantShare(this.sessionService.effectiveArea, billingExportId, id)
					),
		]);

		return {
			...full,
			typeLabelKey: getAllocationTypeLabelKey(full.dtype),
			ordinaryType,
			tenantShareExportId,
			selectionName: selectionName ?? undefined,
			shareName: shareName ?? undefined,
		};
	}
}
